import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BACKEND_URL from 'constants/backendUrl';
import { Civility } from 'core/enums/civility';
import { Politesse } from 'core/enums/politesse';
import Contact from 'core/models/contact';
import Tag, { NewTag } from 'core/models/tag';
import Task from 'core/models/task';
import { DELETE, GET, POST, PUT } from 'utils/http';

// Couche d'accès « état serveur » des contacts via TanStack Query.
// Le cache (staleTime) remplace l'ancien flag Redux `alreadyFetched`, et
// l'invalidation de cette clé remplace les actions Set/Add/Update/Delete + refetch.
export const contactsQueryKey = ['contacts'] as const;

interface ContactsResponse {
    contacts: Contact[];
}

// Endpoint historiquement utilisé par contactsFetcher (ContactsProvider).
export const fetchContacts = async (): Promise<Contact[]> => {
    const response = (await GET(`${BACKEND_URL}/api/contacts`)) as ContactsResponse;

    return response.contacts ?? [];
};

// Hook de liste de base.
export const useContactsQuery = () =>
    useQuery({
        queryKey: contactsQueryKey,
        queryFn: fetchContacts,
    });

// contactsSelector(state) -> useContacts()
export const useContacts = () => useContactsQuery();

// Équivalent de contactByIdSelector(state, id) -> useContactById(id).
export const useContactById = (id: string | undefined) =>
    useQuery({
        queryKey: contactsQueryKey,
        queryFn: fetchContacts,
        select: (contacts: Contact[]) => contacts.find((contact) => contact.id === id),
        enabled: Boolean(id),
    });

// --- Transformation CSV (équivalent de contactsCSVBuffer) ----------------------
const CSV_KEYS = {
    firstName: 'First Name',
    lastName: 'Last Name',
    comments: 'Notes',
    email: 'E-mail Address',
    phoneNumber: 'Business Phone',
    mobileNumber: 'Mobile Phone',
    company: 'Company',
    job: 'Job Title',
    civility: 'Gender (m/f)',
};
const CSV_CIVILITY_MALE_KEY = 'm';
const CSV_CIVILITY_FEMALE_KEY = 'f';

const computeCSVBuffer = (contacts: Contact[]): (string | undefined)[][] => {
    const formattedContacts = contacts.map((contact: Contact) => [
        contact.firstName || '',
        contact.lastName || '',
        contact.comments || '',
        contact.email || '',
        contact.phoneNumber || '',
        contact.mobileNumber || '',
        contact.company || '',
        contact.job || '',
        Civility.Female === contact.civility ? CSV_CIVILITY_FEMALE_KEY : CSV_CIVILITY_MALE_KEY,
    ]);

    formattedContacts.unshift(Object.keys(CSV_KEYS));

    return formattedContacts;
};

// Équivalent de contactsCSVBuffer(state) -> useContactsCSVBuffer().
export const useContactsCSVBuffer = () =>
    useQuery({
        queryKey: contactsQueryKey,
        queryFn: fetchContacts,
        select: computeCSVBuffer,
    });

// À appeler après une mutation pour resynchroniser la liste depuis le serveur.
// Remplace aussi contactsAlreadyFetchedSelector : le cache gère désormais le « déjà chargé ».
export const useInvalidateContacts = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: contactsQueryKey });
};

// --- Mutations -----------------------------------------------------------------

// Payload envoyé au backend pour la création / mise à jour (cf. ContactForm `Inputs`).
export interface ContactInput {
    firstName?: string;
    lastName: string;
    mobileNumber?: string;
    phoneNumber?: string;
    email?: string;
    job?: string;
    company?: string;
    parentId?: string;
    civility: Civility;
    politesse?: Politesse;
    comments?: string;
    resources?: string;
    tags?: Array<Tag | NewTag>;
}

export interface CreateContactResponse {
    contact: Contact;
    task?: Task;
}

export interface UpdateContactResponse {
    updatedContact: Contact;
    updatedRanks: Array<{
        id: string;
        rank: number;
    }>;
}

// Remplace dispatch(AddContact(...)) + POST /api/contact.
export const useCreateContactMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (inputs: ContactInput) =>
            POST<CreateContactResponse>(`${BACKEND_URL}/api/contact`, inputs as unknown as Record<string, unknown>),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsQueryKey }),
    });
};

// Remplace dispatch(UpdateContact(...)) + dispatch(UpdateContactRank(...)) + PUT /api/contact/:id.
// L'API renvoie le contact mis à jour ainsi que les rangs recalculés des descendants ;
// l'invalidation du cache resynchronise la liste (rangs inclus).
export const useUpdateContactMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, inputs }: { id: string; inputs: ContactInput }) =>
            PUT<UpdateContactResponse>(`${BACKEND_URL}/api/contact/${id}`, {
                ...(inputs as unknown as Record<string, unknown>),
            }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsQueryKey }),
    });
};

export const useDeleteContactMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (contactId: string) => DELETE(`${BACKEND_URL}/api/contact/${contactId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsQueryKey }),
    });
};
